import { useCallback, useEffect, useState } from "react";
import { unitsApi, type UnitView } from "../api/catalog";
import type { ApiError } from "../api/client";

/**
 * The unit codebook (`GET /units`, sprint 5k) shared by every screen that picks one — the
 * attribute form and the codebook page. Same shape as `useManufacturers`: fetched once per
 * session, cached at module level; a write (the codebook page, "Nova jedinica" on the
 * attribute form) calls `refresh()` so every open picker sees it.
 */

let cache: UnitView[] | null = null;
let inflight: Promise<UnitView[]> | null = null;
const listeners = new Set<() => void>();

function load(force = false): Promise<UnitView[]> {
  if (!force && cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = unitsApi
    .list()
    .then((list) => {
      cache = list;
      inflight = null;
      for (const l of listeners) l();
      return list;
    })
    .catch((err: unknown) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

/** Test hook: drop the module cache so the next mount fetches again. */
export function resetUnitsCache(): void {
  cache = null;
  inflight = null;
}

export type UnitsHook = {
  /** Sorted by symbol (as the server returns them). Empty while loading or on error. */
  units: UnitView[];
  loading: boolean;
  error: string | null;
  /** Re-fetch after a write; every mounted hook re-renders. */
  refresh: () => Promise<UnitView[]>;
};

export function useUnits(): UnitsHook {
  const [units, setUnits] = useState<UnitView[]>(cache ?? []);
  const [loading, setLoading] = useState(cache === null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const sync = () => { if (!cancelled && cache) setUnits(cache); };
    listeners.add(sync);
    load()
      .then(() => { if (!cancelled) { sync(); setLoading(false); } })
      .catch((err: ApiError) => { if (!cancelled) { setError(err.message); setLoading(false); } });
    return () => { cancelled = true; listeners.delete(sync); };
  }, []);

  const refresh = useCallback(() => load(true), []);
  return { units, loading, error, refresh };
}

/** "kW — kilovat" / "kW": how a unit is offered in a picker and shown in a table. */
export function unitLabel(u: Pick<UnitView, "symbol" | "name">): string {
  return u.name ? `${u.symbol} — ${u.name}` : u.symbol;
}
